import {
  ALL_TARGETS_ID,
  DEFAULT_LOCATIONS,
  DEFAULT_LOCATION_IDS,
  type LocationId,
  type TargetLocation,
} from "./locations";

// Anahtar uygulama adıyla birlikte değiştirilmez: kullanıcının kayıtlı
// konumları ve ayarları bu anahtarın altında duruyor.
const KEY = "pusula.settings.v1";

export interface Coordinate {
  lat: number;
  lon: number;
}

/** Kullanıcının Ayarlar'dan eklediği hedef. */
export interface CustomLocation extends Coordinate {
  id: LocationId;
  name: string;
}

export interface Settings {
  /** Son seçilen hedef ya da ALL_TARGETS_ID. */
  selected: LocationId;
  /** Yerleşik hedeflerin değiştirilmiş koordinatları. */
  overrides: Record<LocationId, Coordinate>;
  /** Kullanıcının eklediği hedefler. */
  custom: CustomLocation[];
  /** Manyetik sapma düzeltmesi (derece, doğuya pozitif). */
  declination: number;
  /** Konum izni yoksa elle girilen kendi konumu. */
  manualPosition: Coordinate | null;
}

export const DEFAULT_SETTINGS: Settings = {
  selected: ALL_TARGETS_ID,
  overrides: {},
  custom: [],
  declination: 0,
  manualPosition: null,
};

/** Eklenebilecek hedef sayısı — seçim çubuğu okunur kalsın diye sınırlı. */
export const MAX_CUSTOM_LOCATIONS = 12;

export function isValidCoordinate(value: unknown): value is Coordinate {
  if (typeof value !== "object" || value === null) return false;
  const { lat, lon } = value as Coordinate;
  return (
    typeof lat === "number" &&
    typeof lon === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lon) <= 180
  );
}

function isValidCustom(value: unknown): value is CustomLocation {
  if (!isValidCoordinate(value)) return false;
  const { id, name } = value as CustomLocation;
  return (
    typeof id === "string" &&
    id.length > 0 &&
    id !== ALL_TARGETS_ID &&
    !DEFAULT_LOCATION_IDS.has(id) &&
    typeof name === "string" &&
    name.trim().length > 0
  );
}

export function loadSettings(): Settings {
  if (typeof localStorage === "undefined") return DEFAULT_SETTINGS;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<Settings>;

    const overrides: Settings["overrides"] = {};
    for (const [id, value] of Object.entries(parsed.overrides ?? {})) {
      if (DEFAULT_LOCATION_IDS.has(id) && isValidCoordinate(value)) {
        overrides[id] = value;
      }
    }

    const custom: CustomLocation[] = [];
    for (const entry of Array.isArray(parsed.custom) ? parsed.custom : []) {
      if (isValidCustom(entry) && !custom.some((c) => c.id === entry.id)) {
        custom.push({
          id: entry.id,
          name: entry.name.trim(),
          lat: entry.lat,
          lon: entry.lon,
        });
      }
      if (custom.length >= MAX_CUSTOM_LOCATIONS) break;
    }

    const known = new Set<string>([
      ALL_TARGETS_ID,
      ...DEFAULT_LOCATION_IDS,
      ...custom.map((c) => c.id),
    ]);

    return {
      selected:
        typeof parsed.selected === "string" && known.has(parsed.selected)
          ? parsed.selected
          : DEFAULT_SETTINGS.selected,
      overrides,
      custom,
      declination:
        typeof parsed.declination === "number" && Number.isFinite(parsed.declination)
          ? Math.max(-45, Math.min(45, parsed.declination))
          : 0,
      manualPosition: isValidCoordinate(parsed.manualPosition)
        ? parsed.manualPosition
        : null,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(settings: Settings): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Depolama kapalıysa (özel mod, kota) ayar kaybı kabul edilebilir.
  }
}

/** Yerleşik hedeflere kullanıcı düzenlemelerini uygular, eklenenleri ekler. */
export function resolveLocations(settings: Settings): TargetLocation[] {
  const builtIn = DEFAULT_LOCATIONS.map((location) => {
    const override = location.fixed ? undefined : settings.overrides[location.id];
    return override ? { ...location, ...override } : location;
  });

  const added = settings.custom.map<TargetLocation>((entry) => ({
    id: entry.id,
    name: entry.name,
    subtitle: "Eklenen konum",
    lat: entry.lat,
    lon: entry.lon,
    custom: true,
  }));

  return [...builtIn, ...added];
}

/** Eklenen hedefler için çakışmayan bir kimlik üretir. */
export function createLocationId(): LocationId {
  const random = Math.random().toString(36).slice(2, 7);
  return `custom-${Date.now().toString(36)}-${random}`;
}

/**
 * "41,0245 29,068" · "41.0245, 29.0680" · Google Maps bağlantısı içindeki
 * "@41.0245,29.068,17z" gibi girdilerden koordinat ayıklar.
 */
export function parseCoordinateInput(input: string): Coordinate | null {
  const text = input.trim();
  if (!text) return null;

  const atMatch = text.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (atMatch) {
    const candidate = { lat: Number(atMatch[1]), lon: Number(atMatch[2]) };
    return isValidCoordinate(candidate) ? candidate : null;
  }

  const queryMatch = text.match(/[?&](?:q|query|ll|daddr)=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (queryMatch) {
    const candidate = { lat: Number(queryMatch[1]), lon: Number(queryMatch[2]) };
    return isValidCoordinate(candidate) ? candidate : null;
  }

  // Ondalık ayırıcı olarak virgül de kabul edilir: "41,0245 29,0680".
  const numbers = text
    .replace(/(\d),(\d)/g, "$1.$2")
    .match(/-?\d+(?:\.\d+)?/g);
  if (!numbers || numbers.length < 2) return null;

  const candidate = { lat: Number(numbers[0]), lon: Number(numbers[1]) };
  return isValidCoordinate(candidate) ? candidate : null;
}
